
import {defineComponent, PropType, reactive, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useToast} from "primevue/usetoast";
import Badge from 'primevue/badge';
import InputText from "primevue/inputtext";
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import {useVuelidate} from "@vuelidate/core";
import {
	isIconFormatCorrect,
	maxLengthOfFifteenValidation,
	whitespaceValidation
} from "@/util/ValidationHelper";
import {ValidationButtonSettingsDto} from "@dex/squeeze-client-ts";
import {ClientManager} from "@/singletons/ClientManager";
import {ToastManager} from "@/util/ToastManager";
import Tooltip from "primevue/tooltip";
import ButtonNameEnum = ValidationButtonSettingsDto.ButtonNameEnum;

export default defineComponent({
	name: "SystemValidationButtonSettingsForm",
	methods: {isIconFormatCorrect, maxLengthOfFifteenValidation},
	components: {
		Badge,
		InputText,
		InputGroup,
		InputGroupAddon,
	},
	directives: {
		'tooltip': Tooltip,
	},
	props: {
		setting: {
			type: Object as PropType<ValidationButtonSettingsDto>,
			default: () => ({}),
		},
	},
	emits: ['load'],
	setup(props, { emit }) {
		const {t} = useI18n();
		const toast = useToast();

		/** Api for validation */
		const validationApi = ClientManager.getInstance().squeeze.validation;

		/** Show Loading on load data */
		const loading = ref(false);

		/** Triggered when (all) field values are invalid */
		const showErrorMessage = ref<boolean>(false);

		/** Triggered the valid of form */
		const isInvalid = ref<boolean>(true);

		/** Current settings of button settings */
		const value = reactive<ValidationButtonSettingsDto>({
			buttonName: props.setting?.buttonName || ButtonNameEnum.Validate,
			description: props.setting?.description || '',
			color: props.setting?.color || '',
			icon: props.setting?.icon || '',
		});

		/** Determines the required rules for validation */
		const rules = {
			description: { maxLengthOfFifteenValidation },
			color: { },
			icon: { isIconFormatCorrect, whitespaceValidation },
		};

		/** Use Vuelidate */
		const v$ = useVuelidate(rules as any, value);

		/** Save the validation button settings */
		const saveValidationButtonSettings = () => {
			if (v$.value.$invalid) {
				showErrorMessage.value = true;
				return
			}

			showErrorMessage.value = false;
			loading.value = true;

			validationApi.putValidationButtonSettingsByName(value.buttonName as any, value)
				.then(() => {
					ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.System.SaveValidationButtonSettingsMessage'));
				})
				.catch(response => response.json().then((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				})
		}

		/** Reset the validation button settings by button name */
		const resetValidationButtonSettings = () => {
			loading.value = true;
			validationApi.deleteValidationButtonSettings(value.buttonName as any)
				.then(() => {
					value.description = '';
					value.color = '';
					value.icon = '';
				})
				.catch(response => response.json().then((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				})
		}

		/**
		 * Triggered when color changed
		 * @param color
		 */
		const onChangeColor = (color: string) => {
			value.color = color;
		}

		/** Go to the material design icons page, when badge clicked */
		const goToMaterialDesignIcons = () => {
			window.open('https://pictogrammers.github.io/@mdi/font/5.4.55/');
		}

		/** Watch loading property */
		watch(() => loading.value, () => {
			emit('load', loading.value);
		})

		/** Watch prop at set value object, because props are not allowed to be mutated */
		watch(() => props.setting, (newVal) => {
			if (newVal) {
				Object.assign(value, newVal);
			}
		}, { immediate: true, deep: true })

		return {
			t,
			toast,
			loading,
			showErrorMessage,
			isInvalid,
			value,
			rules,
			v$,
			saveValidationButtonSettings,
			resetValidationButtonSettings,
			onChangeColor,
			goToMaterialDesignIcons,
		}
	},
})
